import React, { useState } from "react";
import {
  useGetSalesProductsQuery,
  useRemoveProductFromSalesMutation,
} from "../redux/apislice";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Player } from "@lottiefiles/react-lottie-player";


const OnSalesProducts = () => {
  const { saleId } = useParams();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, error, isLoading, refetch } = useGetSalesProductsQuery({
    salesId: saleId,
    page,
    limit,
  });

  const [removeProductFromSales] = useRemoveProductFromSalesMutation();
  const [removingProductId, setRemovingProductId] = useState(null); 
  const getStatusStyle = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-300 text-green-800";
      case "inactive":
        return "bg-gray-300 text-gray-800";
      case "rejected":
        return "bg-red-300 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleDeleteProduct = async (productId, salesId) => {
    try {
      setRemovingProductId(productId); 
      const res = await removeProductFromSales({ productId, salesId }).unwrap();
      toast.success(res.message || "Product removed from sales");
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to remove product from sales");
    } finally {
      setRemovingProductId(null);
    }
  };

  if (isLoading) {
      return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100%',
          }}>
        <Player
              autoplay
              loop
              src="https://assets1.lottiefiles.com/packages/lf20_raiw2hpe.json"
              style={{ height: '150px', width: '150px' }}
            />
          </div>
        );
      }
  if (error)
    return (
      <div className="text-red-500 text-center p-4">
        Error fetching sales products
      </div>
    );

  const products = data?.data || [];
  const currentPage = data?.currentPage || page;
  const isNext = data?.isNext || false;

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Products in #{saleId}</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 p-4">
        {products.length > 0 ? (
          products.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-300 flex flex-col"
            >
              <img
                src={item.product.images[0]?.url || "/placeholder-product.jpg"}
                alt={item.product.title}
                className="w-full h-48 object-cover rounded-t-2xl"
                onError={(e) => {
                  e.target.src = "/placeholder-product.jpg";
                }}
              />
              <div className="p-4 flex-grow">
                <h2 className="text-xl font-semibold text-gray-800 mb-1 truncate">
                  {item.product.title}
                </h2>
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                  {item.product.shortdes}
                </p>

                <div className="flex justify-between items-center mb-2">
                  <span className="text-green-600 font-bold text-lg">
                    Rs {item.product.on_price}
                  </span>
                  <span className="line-through text-red-400 text-sm">
                    Rs {item.product.off_price}
                  </span>
                </div>

                <div className="flex justify-between items-center mb-4">
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${getStatusStyle(
                      item.status
                    )}`}
                  >
                    {item.status}
                  </span>
                  {item.discount > 0 && (
                    <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                      {item.discount}% OFF
                    </span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <button
                  onClick={() =>
                    handleDeleteProduct(item.productId, item.salesId)
                  }
                  disabled={removingProductId === item.productId}
                  className="w-full py-2 bg-gray-900 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  {removingProductId === item.productId
                    ? "Removing..."
                    : "Remove Product from Sale"}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center text-gray-600">
            No products found.
          </div>
        )}
      </div>

      {(page > 1 || isNext) && (
        <div className="mt-8 flex justify-center items-center gap-4">
          {page > 1 && (
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
            >
              Previous
            </button>
          )}

          <span className="text-gray-700 font-medium">Page {page}</span>

          {isNext && (
            <button
              onClick={() => setPage((prev) => prev + 1)}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
            >
              Next
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OnSalesProducts;
